// Updated App.js with fixes for equipment loading and display issues
import React, { useState, useEffect } from 'react';
import './App.css';
import './buttonAnimations.css'; // Import button animations
import { getAllEquipment, updateEquipment, addEquipment, deleteEquipment, equipmentCollectionRef } from './services/equipmentService';
import { getAllBookings, getStudentBookings, updateBooking } from './services/bookingService';
import { getCurrentUser, logOut } from './services/authService';
import { uploadFile } from './services/fileService';
import EnhancedBookingsTable from './components/EnhancedBookingsTable';
import EquipmentManager from './components/EquipmentManager';
import Login from './components/Login';
import Register from './components/Register';
import BookingHistory from './components/BookingHistory';
import { addBooking } from './services/bookingService';
import { db } from './firebase';
import { collection, onSnapshot, query, where, orderBy, doc, updateDoc, getDoc, getDocs, addDoc, serverTimestamp, Timestamp, writeBatch, limit, startAfter, deleteDoc, setDoc, increment } from 'firebase/firestore';
import { initializeRippleButtons } from './utils/buttonEffects'; // Import ripple effect utility
import { onAuthStateChanged } from 'firebase/auth';
import { signOut } from 'firebase/auth';
import { toDateOnly, isStockHoldingStatus, calculateEquipmentAvailability, calculateMultiItemAvailability } from './utils/availabilityEngine';

function App() {
  const STUDENT_EDIT_LOCK_HOURS = 24;
  // State for user authentication and role
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [authView, setAuthView] = useState('login'); // 'login' or 'register'
  const [error, setError] = useState(null);
  const [hoveredItem, setHoveredItem] = useState(null); // Add hover state
  const [isEquipmentManagerCollapsed, setIsEquipmentManagerCollapsed] = useState(false); // Add state for collapsing
  
  // State for app content
  const [activeTab, setActiveTab] = useState('student');
  const [equipment, setEquipment] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [availabilityBookings, setAvailabilityBookings] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [returnDate, setReturnDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() + 1); // Default to next day
    return date;
  });
  const [selectedEquipment, setSelectedEquipment] = useState([]);
  const [activeCategory, setActiveCategory] = useState('');
  // New state for student booking view (current/previous)
  const [studentBookingView, setStudentBookingView] = useState('current');
  
  // New state for additional booking fields
  const todayStr = new Date().toISOString().split('T')[0];
  const [bookingDetails, setBookingDetails] = useState({
    productionName: '',
    className: '',
    teacherOnShoot: '',
    notes: '',
    startDate: todayStr,
    endDate: todayStr,
    riskAssessmentFile: null,
    shootingScheduleFile: null
  });

  const teachers = [
    { id: 'oren', name: 'Oren' },
    { id: 'wendy', name: 'Wendy' },
    { id: 'eden', name: 'Eden' },
    { id: 'sera', name: 'Sera' },
    { id: 'morgan', name: 'Morgan' },
    { id: 'rohan', name: 'Rohan' }
  ];

  // State for user authentication and role
  const [isBookingLoading, setIsBookingLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [bookingSuccess, setBookingSuccess] = useState(false);

  // State for file upload status
  const [fileUploadStatus, setFileUploadStatus] = useState({
    riskAssessment: { uploading: false, error: null },
    shootingSchedule: { uploading: false, error: null }
  });

  // State for staff view
  const [staffView, setStaffView] = useState('bookings');

  // State for booking history
  const [isStaffViewHistory, setIsStaffViewHistory] = useState(false);

  // Add this to the state declarations at the top of the component
  const [isCalendarCollapsed, setIsCalendarCollapsed] = useState(true);

  // Effect to check for authenticated user on component mount
  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        const currentUser = await getCurrentUser();
        if (currentUser) {
          setUser(currentUser);
          setActiveTab(currentUser.role === 'staff' ? 'staff' : 'student');
        } else {
          console.log("No authenticated user found");
        }
      } catch (error) {
        setError('Authentication error. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };
    
    checkAuthStatus();
  }, []);

  // Setup real-time listener for equipment when user is authenticated
  useEffect(() => {
    if (!user) {
      console.log("No user authenticated, skipping equipment listener setup");
      setEquipment([]);
      return;
    }
    
    try {
      console.log("Setting up equipment listener for user:", user);
      console.log("Firebase db instance:", db);
      const unsubscribe = onSnapshot(equipmentCollectionRef, (snapshot) => {
        console.log("Equipment snapshot received:", snapshot.size, "items");
        const updatedEquipment = [];
        snapshot.forEach((doc) => {
          const data = doc.data();
          console.log("Equipment item:", { id: doc.id, ...data });
          updatedEquipment.push({
            id: doc.id,
            ...data
          });
        });
        
        if (updatedEquipment.length > 0) {
          console.log("Updating equipment state with", updatedEquipment.length, "items");
          setEquipment(updatedEquipment);
          
          const uniqueCategories = [...new Set(updatedEquipment
            .filter(item => item && item.category)
            .map(item => item.category))];
          
          if (uniqueCategories.length > 0) {
            const cameraCategory = uniqueCategories.find(cat => cat === "Camera");
            setActiveCategory(cameraCategory || uniqueCategories[0]);
          }
        } else {
          console.log("No equipment items found in snapshot");
        }
      }, (error) => {
        console.error("Error in equipment listener:", error);
        setError('Error listening to equipment updates');
      });
      
      return () => {
        console.log("Cleaning up equipment listener");
        unsubscribe();
      };
    } catch (error) {
      console.error("Error setting up equipment listener:", error);
      setError('Error setting up equipment updates');
    }
  }, [user]);

  // Setup real-time listener for bookings when user is authenticated
  useEffect(() => {
    if (!user) {
      console.log("No user authenticated, skipping bookings listener setup");
      setBookings([]);
      return;
    }
    
    try {
      if (user.role === 'staff') {
        // Staff: listen to all bookings
        const bookingsQuery = query(
          collection(db, "bookings"),
          orderBy("startDate", "asc")
        );
        const unsubscribe = onSnapshot(bookingsQuery, (snapshot) => {
          const updatedBookings = [];
          snapshot.forEach((doc) => {
            updatedBookings.push({ id: doc.id, ...doc.data() });
          });
          setBookings(updatedBookings);
          setAvailabilityBookings(updatedBookings);
        }, (error) => {
          console.error("Error in bookings listener:", error);
        });
        return () => unsubscribe();
      } else {
        // Students: only listen to their own bookings
        const myBookingsQuery = query(
          collection(db, "bookings"),
          where("studentId", "==", user.phoneNumber),
          orderBy("createdAt", "desc")
        );
        
        const unsubscribe = onSnapshot(myBookingsQuery, (snapshot) => {
          const updatedBookings = [];
          snapshot.forEach((doc) => {
            updatedBookings.push({ id: doc.id, ...doc.data() });
          });
          setBookings(updatedBookings);
          console.log("Bookings loaded for student:", updatedBookings);
        }, (error) => {
          console.error("Error in bookings listener:", error);
        });
        
        return () => unsubscribe();
      }
    } catch (error) {
      console.error("Error setting up bookings listener:", error);
      setBookings([]);
    }
  }, [user]);


  // Availability must be computed from all bookings, not just the student's own bookings.
  // We filter active statuses client-side to avoid status-case mismatches.
  useEffect(() => {
    if (!user || user.role === 'staff') return;

    try {
      // Students cannot safely subscribe to all bookings due security rules.
      // Use rule-safe queries and merge them client-side:
      // 1) all approved bookings (globally visible to students)
      // 2) student's own pending bookings
      const approvedBookingsQuery = query(
        collection(db, "bookings"),
        where("status", "==", "Approved")
      );
      const myPendingBookingsQuery = query(
        collection(db, "bookings"),
        where("studentId", "==", user.phoneNumber),
        where("status", "==", "Pending")
      );

      let approvedBookingsCache = [];
      let myPendingBookingsCache = [];
      const syncAvailabilityBookings = () => {
        const mergedMap = new Map();
        [...approvedBookingsCache, ...myPendingBookingsCache].forEach((booking) => {
          if (booking?.id) mergedMap.set(booking.id, booking);
        });
        setAvailabilityBookings(Array.from(mergedMap.values()));
      };

      const unsubscribeApproved = onSnapshot(approvedBookingsQuery, (snapshot) => {
        const approved = [];
        snapshot.forEach((doc) => {
          approved.push({ id: doc.id, ...doc.data() });
        });
        approvedBookingsCache = approved;
        syncAvailabilityBookings();
      }, (error) => {
        console.error("Error in approved availability listener:", error);
      });

      const unsubscribeMyPending = onSnapshot(myPendingBookingsQuery, (snapshot) => {
        const myPending = [];
        snapshot.forEach((doc) => {
          myPending.push({ id: doc.id, ...doc.data() });
        });
        myPendingBookingsCache = myPending;
        syncAvailabilityBookings();
      }, (error) => {
        console.error("Error in pending availability listener:", error);
      });

      return () => {
        unsubscribeApproved();
        unsubscribeMyPending();
      };
    } catch (error) {
      console.error("Error setting up availability listener:", error);
    }
  }, [user]);

  // Debug logging for equipment and categories
  useEffect(() => {
    if (Array.isArray(equipment)) {
      console.log("Current equipment state:", equipment.length, "items");
      
      // Log the categories
      const cats = [...new Set(equipment
        .filter(item => item && item.category)
        .map(item => item.category))];
      console.log("Available categories:", cats);
    } else {
      console.warn("Equipment is not an array:", equipment);
    }
  }, [equipment]);

  // Get unique categories from equipment with safety checks
  const categories = Array.isArray(equipment) ? 
    [...new Set(equipment
      .filter(item => item && item.category) // Make sure category exists
      .map(item => item.category))] : [];

  // Sort categories in the specified order
  const categoryOrder = [
    'Camera',
    'Lenses',
    'Camera Extras',
    'Microphones',
    'Audio',
    'Lighting',
    'Rigging and Grip'
  ];

  const sortedCategories = categories.sort((a, b) => {
    const indexA = categoryOrder.indexOf(a);
    const indexB = categoryOrder.indexOf(b);
    // If both categories are in the order list, sort by their position
    if (indexA !== -1 && indexB !== -1) {
      return indexA - indexB;
    }
    // If only one category is in the order list, prioritize it
    if (indexA !== -1) return -1;
    if (indexB !== -1) return 1;
    // If neither category is in the order list, maintain original order
    return 0;
  });
  
  // Calculate duration between two dates in days
  const calculateDuration = (startDate, endDate) => {
    const diffTime = Math.abs(endDate - startDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };
  
  // Handle login success
  const handleLoginSuccess = (userInfo) => {
    console.log("Login successful:", userInfo);
    setUser(userInfo);
    setActiveTab(userInfo.role === 'staff' ? 'staff' : 'student');
  };
  
  // Handle registration success
  const handleRegistrationSuccess = (userInfo) => {
    console.log("Registration successful:", userInfo);
    setUser(userInfo);
    setActiveTab('student'); // New registrations are always students
  };
  
  // Logout function
  const handleLogout = async () => {
    try {
      await logOut();
      setUser(null);
      setAuthView('login');
      setSelectedEquipment([]);
      // Reset booking details
      setBookingDetails({
        productionName: '',
        className: '',
        teacherOnShoot: '',
        notes: '',
        startDate: todayStr,
        endDate: todayStr,
        riskAssessmentFile: null,
        shootingScheduleFile: null
      });
    } catch (error) {
      console.error("Error during logout:", error);
    }
  };
  
  // Handle booking update from EnhancedBookingsTable
  const handleBookingUpdated = async () => {
    // Bookings are now updated automatically via the real-time listener
    // This function is kept for backwards compatibility
    console.log("Booking updated callback triggered");
  };

  const parseDateOnly = (dateString) => {
    return toDateOnly(dateString);
  };

  const getRelevantBookingsForEquipment = (equipmentId, excludeBookingId = null) => {
    const sourceBookings = user?.role === 'staff' ? bookings : availabilityBookings;
    return sourceBookings.filter((booking) => {
      if (!booking || booking.id === excludeBookingId || !Array.isArray(booking.items)) {
        return false;
      }
      const isBlockingStatus = isStockHoldingStatus(booking.status);
      return isBlockingStatus && booking.items.some((item) => item.equipmentId === equipmentId);
    });
  };

  const getBookedQuantityForRange = (equipmentId, startDate, endDate, excludeBookingId = null) => {
    const requestedStart = parseDateOnly(startDate);
    const requestedEnd = parseDateOnly(endDate);
    if (!requestedStart || !requestedEnd) return 0;

    const relevantBookings = getRelevantBookingsForEquipment(equipmentId, excludeBookingId);
    let totalBooked = 0;
    const overlapRows = [];

    relevantBookings.forEach((booking) => {
      const bookingStart = parseDateOnly(booking.startDate);
      const bookingEnd = parseDateOnly(booking.endDate);
      if (!bookingStart || !bookingEnd) return;

      const overlaps = requestedStart <= bookingEnd && requestedEnd >= bookingStart;
      const bookedItem = booking.items.find((item) => item.equipmentId === equipmentId);
      const bookedQty = Number(bookedItem?.quantity || 0);

      overlapRows.push({
        bookingId: booking.id,
        status: booking.status,
        startDate: booking.startDate,
        endDate: booking.endDate,
        bookedQty,
        overlaps
      });

      if (overlaps) {
        totalBooked += bookedQty;
      }
    });

    return totalBooked;
  };

  const getAvailableQtyForDates = (equipmentId, startDate, endDate, excludeBookingId = null) => {
    const equipmentItem = equipment.find((eq) => eq.id === equipmentId);
    if (!equipmentItem) return 0;
    if (!startDate || !endDate) return Number(equipmentItem.availableQty || 0);

    const bookingsSource = user?.role === 'staff' ? bookings : availabilityBookings;
    const availability = calculateEquipmentAvailability({
      equipmentItem,
      bookings: bookingsSource,
      startDate,
      endDate,
      excludeBookingId
    });
    return availability.availableQty;
  };

  const canFulfilBookingItems = (items, startDate, endDate, excludeBookingId = null) => {
    const equipmentById = Object.fromEntries((equipment || []).map((item) => [item.id, item]));
    const bookingsSource = user?.role === 'staff' ? bookings : availabilityBookings;
    return calculateMultiItemAvailability({
      selectedItems: items,
      equipmentById,
      bookings: bookingsSource,
      startDate,
      endDate,
      excludeBookingId
    });
  };

  const getOverlapBookingsForRange = (equipmentId, startDate, endDate, excludeBookingId = null) => {
    const requestedStart = parseDateOnly(startDate);
    const requestedEnd = parseDateOnly(endDate);
    if (!requestedStart || !requestedEnd) return [];

    return getRelevantBookingsForEquipment(equipmentId, excludeBookingId).filter((booking) => {
      const bookingStart = parseDateOnly(booking.startDate);
      const bookingEnd = parseDateOnly(booking.endDate);
      if (!bookingStart || !bookingEnd) return false;
      return requestedStart <= bookingEnd && requestedEnd >= bookingStart;
    });
  };
  
  // Handle selecting equipment
  const handleSelectEquipment = (item) => {
    // Check if item is already selected
    const existingIndex = selectedEquipment.findIndex(
      selected => selected.equipmentId === item.id
    );
    
    if (existingIndex >= 0) {
      // Item already selected - remove it
      const updatedSelection = [...selectedEquipment];
      updatedSelection.splice(existingIndex, 1);
      setSelectedEquipment(updatedSelection);
    } else {
      // Add new item with quantity 1
      setSelectedEquipment([
        ...selectedEquipment,
        {
          equipmentId: item.id,
          equipmentName: item.name,
          quantity: 1,
          maxQuantity: item.availableQty
        }
      ]);
    }
  };
  
  // Handle changes to booking details
  const handleBookingDetailsChange = (e) => {
    const { name, value } = e.target;
    setBookingDetails(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Update equipment quantity
  const updateEquipmentQuantity = (equipmentId, quantity) => {
    const startDate = bookingDetails.startDate;
    const endDate = bookingDetails.endDate || bookingDetails.startDate;
    const dynamicMax = Math.max(1, getAvailableQtyForDates(equipmentId, startDate, endDate));
    const normalizedQuantity = Number.isFinite(quantity) ? quantity : 1;
    const clampedQuantity = Math.max(1, Math.min(normalizedQuantity, dynamicMax));

    const updatedSelection = selectedEquipment.map(item => {
      if (item.equipmentId === equipmentId) {
        return { ...item, quantity: clampedQuantity };
      }
      return item;
    });
    setSelectedEquipment(updatedSelection);
  };
  
  // Handle file selection
  const handleFileSelect = (e, fileType) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!validTypes.includes(file.type)) {
        setFileUploadStatus(prev => ({
          ...prev,
          [fileType]: { uploading: false, error: 'Please upload a PDF or Word document' }
        }));
        return;
      }

      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setFileUploadStatus(prev => ({
          ...prev,
          [fileType]: { uploading: false, error: 'File size must be less than 10MB' }
        }));
        return;
      }

      setBookingDetails(prev => ({
        ...prev,
        [fileType]: file
      }));
      setFileUploadStatus(prev => ({
        ...prev,
        [fileType]: { uploading: false, error: null }
      }));
    }
  };
  
  // Book equipment function
  const bookEquipment = async () => {
    console.log("bookEquipment called");
    if (selectedEquipment.length === 0) {
      setError('Please select at least one equipment item');
      return;
    }

    // Validate required fields
    if (!bookingDetails.productionName || !bookingDetails.className || !bookingDetails.startDate || !bookingDetails.endDate) {
      setError('Please fill in all required fields');
      return;
    }

    // Validate dates
    const startDate = new Date(bookingDetails.startDate);
    const endDate = new Date(bookingDetails.endDate);
    if (startDate > endDate) {
      setError('End date must be after start date');
      return;
    }

    setIsBookingLoading(true);
    setBookingSuccess(false);
    try {
      // Upload files if they exist
      let riskAssessmentUrl = null;
      let shootingScheduleUrl = null;

      if (bookingDetails.riskAssessmentFile) {
        setFileUploadStatus(prev => ({
          ...prev,
          riskAssessment: { uploading: true, error: null }
        }));
        const riskResult = await uploadFile(bookingDetails.riskAssessmentFile, 'bookings/risk-assessments');
        if (!riskResult.success) {
          throw new Error('Failed to upload risk assessment file');
        }
        riskAssessmentUrl = riskResult.url;
      }

      if (bookingDetails.shootingScheduleFile) {
        setFileUploadStatus(prev => ({
          ...prev,
          shootingSchedule: { uploading: true, error: null }
        }));
        const scheduleResult = await uploadFile(bookingDetails.shootingScheduleFile, 'bookings/shooting-schedules');
        if (!scheduleResult.success) {
          throw new Error('Failed to upload shooting schedule file');
        }
        shootingScheduleUrl = scheduleResult.url;
      }

      // Debug logging for user data
      console.log("Current user data:", {
        uid: user?.uid,
        phoneNumber: user?.phoneNumber,
        name: user?.name,
        role: user?.role
      });

      // Format dates to YYYY-MM-DD
      const formattedStartDate = bookingDetails.startDate.split('T')[0];
      const formattedEndDate = bookingDetails.endDate.split('T')[0];
      
      const newBooking = {
        studentId: user.phoneNumber,
        studentName: user.name,
        items: selectedEquipment,
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        status: 'Pending',
        checkedOutItems: {},
        checkedInItems: {},
        productionName: bookingDetails.productionName,
        className: bookingDetails.className,
        teacherOnShoot: bookingDetails.teacherOnShoot || null,
        notes: bookingDetails.notes || '',
        riskAssessmentUrl,
        shootingScheduleUrl,
        projectType: 'Student Project'
      };
      
      // Debug logging for booking data
      console.log("Booking data validation:", {
        hasStudentId: !!newBooking.studentId,
        studentIdLength: newBooking.studentId?.length,
        hasStudentName: !!newBooking.studentName,
        studentNameLength: newBooking.studentName?.length,
        hasItems: Array.isArray(newBooking.items),
        itemsLength: newBooking.items?.length,
        startDate: newBooking.startDate,
        endDate: newBooking.endDate,
        hasProductionName: !!newBooking.productionName,
        productionNameLength: newBooking.productionName?.length,
        hasClassName: !!newBooking.className,
        classNameLength: newBooking.className?.length,
        hasProjectType: !!newBooking.projectType,
        projectTypeLength: newBooking.projectType?.length
      });
      
      console.log("Full booking data being sent:", newBooking);
      
      const response = await addBooking(newBooking);
      console.log("Booking creation response:", response);
      
      if (response && response.success) {
        setBookingSuccess(true);
        // Reset form
        setSelectedEquipment([]);
        setBookingDetails({
          productionName: '',
          className: '',
          teacherOnShoot: '',
          notes: '',
          startDate: todayStr,
          endDate: todayStr,
          riskAssessmentFile: null,
          shootingScheduleFile: null
        });
      } else {
        setBookingSuccess(false);
        setError('Failed to submit booking request. Please try again.');
        console.error("Booking creation failed:", response);
      }
    } catch (error) {
      setBookingSuccess(false);
      setError('Failed to submit booking request. Please try again.');
      console.error("Error booking equipment:", error);
      // Log the full error object
      console.error("Full error details:", {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
    } finally {
      setIsBookingLoading(false);
      setFileUploadStatus({
        riskAssessment: { uploading: false, error: null },
        shootingSchedule: { uploading: false, error: null }
      });
    }
  };
  
  // Handle booking approval
  const handleApproveBooking = async (bookingId) => {
    try {
      // Find the booking to be approved
      const bookingToApprove = bookings.find(b => b.id === bookingId);
      if (!bookingToApprove) {
        console.error("Booking not found:", bookingId);
        return;
      }

      if (bookingToApprove.status !== 'Pending') {
        console.error("Invalid status transition:", bookingToApprove.status, "-> Approved");
        return;
      }
      
      // Update booking status to Approved
      const response = await updateBooking(bookingId, { status: 'Approved' });
      
      if (response.success) {
        // Update equipment available quantity in Firestore
        for (const bookedItem of bookingToApprove.items) {
          const equipmentItem = equipment.find(item => item.id === bookedItem.equipmentId);
          if (equipmentItem) {
            const newAvailableQty = equipmentItem.availableQty - bookedItem.quantity;
            await updateEquipment(equipmentItem.id, {
              ...equipmentItem,
              availableQty: newAvailableQty
            });
          }
        }
        
        // Update local bookings state
        const updatedBookings = bookings.map(booking => 
          booking.id === bookingId 
            ? { ...booking, status: 'Approved' }
            : booking
        );
        setBookings(updatedBookings);
      }
    } catch (error) {
      console.error("Error approving booking:", error);
    }
  };

  // Handle booking rejection
  const handleRejectBooking = async (bookingId) => {
    try {
      // Find the booking to be rejected
      const bookingToReject = bookings.find(b => b.id === bookingId);
      if (!bookingToReject) {
        console.error("Booking not found:", bookingId);
        return;
      }

      if (!['Pending', 'Approved'].includes(bookingToReject.status)) {
        console.error("Invalid status transition:", bookingToReject.status, "-> Rejected");
        return;
      }
      
      // Update booking status to Rejected
      const response = await updateBooking(bookingId, { status: 'Rejected' });
      
      if (response.success) {
        // If the booking was previously approved, we need to restore the equipment quantities
        if (bookingToReject.status === 'Approved') {
          // Restore equipment available quantity in Firestore
          for (const bookedItem of bookingToReject.items) {
            const equipmentItem = equipment.find(item => item.id === bookedItem.equipmentId);
            if (equipmentItem) {
              const newAvailableQty = equipmentItem.availableQty + bookedItem.quantity;
              await updateEquipment(equipmentItem.id, {
                ...equipmentItem,
                availableQty: newAvailableQty
              });
            }
          }
          
          // Update local state after successful database update
          const updatedEquipment = equipment.map(item => {
            const bookedItem = bookingToReject.items.find(
              bookedItem => bookedItem.equipmentId === item.id
            );
            
            if (bookedItem) {
              return {
                ...item,
                availableQty: item.availableQty + bookedItem.quantity
              };
            }
            return item;
          });
          
          setEquipment(updatedEquipment);
          
          // No need to call fetchEquipment() here as we've already updated the local state
        }
        
        // Update local bookings state
        const updatedBookings = bookings.map(booking => 
          booking.id === bookingId 
            ? { ...booking, status: 'Rejected' }
            : booking
        );
        setBookings(updatedBookings);
      }
    } catch (error) {
      console.error("Error rejecting booking:", error);
    }
  };
  
  // Return equipment function
  const returnEquipment = async (bookingId) => {
    const booking = bookings.find(b => b.id === bookingId);
    if (!booking) return;
    
    try {
      if (booking.status !== 'Approved') {
        console.error("Invalid status transition:", booking.status, "-> Returned");
        return;
      }
      console.log("Returning equipment for booking:", bookingId);
      // Mark booking as returned
      await updateBooking(bookingId, { 
        status: 'Returned',
        returnedDate: new Date().toISOString()
      });
      
      // Update equipment available quantity in Firestore
      for (const returnedItem of booking.items) {
        const equipmentItem = equipment.find(item => item.id === returnedItem.equipmentId);
        if (equipmentItem) {
          const newAvailableQty = equipmentItem.availableQty + returnedItem.quantity;
          await updateEquipment(equipmentItem.id, {
            ...equipmentItem,
            availableQty: newAvailableQty
          });
        }
      }
      
      // Update local state after successful database update
      const updatedEquipment = equipment.map(item => {
        const returnedItem = booking.items.find(
          bookedItem => bookedItem.equipmentId === item.id
        );
        
        if (returnedItem) {
          return {
            ...item,
            availableQty: item.availableQty + returnedItem.quantity
          };
        }
        return item;
      });
      
      setEquipment(updatedEquipment);
    } catch (error) {
      console.error("Error returning equipment:", error);
    }
  };
  
  // Calculate if a booking button should be disabled
  const isBookingDisabled = () => {
    return !bookingDetails.productionName || 
           !bookingDetails.className || 
           !bookingDetails.startDate || 
           !bookingDetails.endDate;
  };
  
  // Get equipment for active category with safety checks
  const categoryEquipment = activeCategory && Array.isArray(equipment) ? 
    equipment.filter(item => item && item.category === activeCategory) : [];
  
  // UPDATED: Simplified booking return status check
  const isBookingReturned = (booking) => {
    if (!booking) return false;
    
    // A booking is only considered "returned" if it has BOTH:
    // 1. It's explicitly marked as "Returned" in status
    // 2. It has a returnedDate property
    return booking.status === 'Returned' && booking.returnedDate;
  };

  // For filtering myBookings and other user-dependent logic, use optional chaining or null checks
  const myBookings = user && user.phoneNumber
    ? bookings.filter(booking => booking.studentId === user.phoneNumber)
    : [];

  // For equipment availability logic (all approved bookings):
  const allApprovedBookings = bookings.filter(
    booking => booking.status === 'Approved'
  );

  // Use myBookings for the student's bookings view
  const currentBookings = Array.isArray(myBookings) ? 
    myBookings.filter(booking => booking.status !== 'Returned') : [];
    
  const previousBookings = Array.isArray(myBookings) ? 
    myBookings.filter(booking => booking.status === 'Returned') : [];

  // Debug logging for bookings after filtering
  useEffect(() => {
    if (Array.isArray(bookings) && bookings.length > 0) {
      console.log(`Bookings divided: ${currentBookings.length} current, ${previousBookings.length} previous`);
      console.log("Current bookings statuses:", currentBookings.map(b => b.status).join(', '));
    }
  }, [bookings, currentBookings, previousBookings]);

  // Add this inside the App component, after other useEffect hooks
  useEffect(() => {
    // Initialize ripple effects for buttons
    const cleanup = initializeRippleButtons();
    
    // Cleanup function to remove event listeners
    return cleanup;
  }, []);

  // Function to check if equipment is available for selected dates
  const isEquipmentAvailableForDates = (equipmentId, startDate, endDate, requestedQty = 1, excludeBookingId = null) => {
    if (!equipmentId || !startDate || !endDate) return false;

    const equipmentItem = equipment.find(e => e.id === equipmentId);
    if (!equipmentItem) return false;

    // Corrected logic:
    // 1) parse date-only values safely
    // 2) count only overlapping Pending/Approved bookings
    // 3) compare remaining stock against requested quantity
    const bookingsSource = user?.role === 'staff' ? bookings : availabilityBookings;
    const availability = calculateEquipmentAvailability({
      equipmentItem,
      bookings: bookingsSource,
      startDate,
      endDate,
      excludeBookingId
    });
    return availability.availableQty >= Number(requestedQty || 0);
  };

  // Get equipment availability status
  const getEquipmentAvailabilityStatus = (equipment, startDate, endDate) => {
    if (!equipment) return { status: 'Unknown', availableQty: 0 };

    const bookingsSource = user?.role === 'staff' ? bookings : availabilityBookings;
    const calculatedAvailability = calculateEquipmentAvailability({
      equipmentItem: equipment,
      bookings: bookingsSource,
      startDate,
      endDate
    });
    const rangeAvailableQty = calculatedAvailability.availableQty;
    const availability = {
      status: 'Unknown',
      availableQty: rangeAvailableQty,
      bookedQty: calculatedAvailability.bookedQty,
      hasOverdueUnreturned: Boolean(calculatedAvailability.hasOverdueUnreturned)
    };

    if (startDate && endDate) {
      const overlapBookings = calculatedAvailability.overlappingBookings || [];

      if (availability.availableQty > 0) {
        availability.status = 'Available';
      } else if (availability.hasOverdueUnreturned) {
        availability.status = 'Out of Stock (Awaiting staff check-in)';
      } else if (overlapBookings.length > 0) {
        // Availability is determined strictly by bookings that overlap the requested date period.
        const latestOverlapEnd = overlapBookings.reduce((latest, booking) => {
          const bookingEnd = parseDateOnly(booking.endDate);
          if (!latest) return bookingEnd;
          return bookingEnd > latest ? bookingEnd : latest;
        }, null);

        const nextAvailableDate = latestOverlapEnd ? new Date(latestOverlapEnd) : null;
        if (nextAvailableDate) {
          nextAvailableDate.setDate(nextAvailableDate.getDate() + 1);
          const day = nextAvailableDate.getDate().toString().padStart(2, '0');
          const month = (nextAvailableDate.getMonth() + 1).toString().padStart(2, '0');
          const year = nextAvailableDate.getFullYear();
          availability.status = `Out of Stock (Available from ${day}/${month}/${year})`;
        } else {
          availability.status = 'Out of Stock';
        }
      } else {
        availability.status = 'Out of Stock';
      }
    } else {
      if (availability.availableQty > 0) {
        availability.status = 'Available';
      } else if ((calculatedAvailability.overlappingBookings || []).length > 0) {
        const sortedOverlaps = [...calculatedAvailability.overlappingBookings].sort((a, b) => {
          const aEnd = parseDateOnly(a.endDate);
          const bEnd = parseDateOnly(b.endDate);
          return (aEnd ? aEnd.getTime() : 0) - (bEnd ? bEnd.getTime() : 0);
        });
        const nextAvailableDate = parseDateOnly(sortedOverlaps[0].endDate);
        nextAvailableDate.setDate(nextAvailableDate.getDate() + 1);
        const day = nextAvailableDate.getDate().toString().padStart(2, '0');
        const month = (nextAvailableDate.getMonth() + 1).toString().padStart(2, '0');
        const year = nextAvailableDate.getFullYear();
        availability.status = `Out of Stock (Available from ${day}/${month}/${year})`;
      } else {
        availability.status = 'Out of Stock';
      }
    }

    return availability;
  };

  // Update the unavailableSelectedEquipment check to use the improved logic
  const unavailableSelectedEquipment = selectedEquipment.filter(item => {
    // Use the correct start and end date from bookingDetails
    const startDate = bookingDetails.startDate;
    const endDate = bookingDetails.endDate || bookingDetails.startDate;
    // Use the improved isEquipmentAvailableForDates logic
    return !isEquipmentAvailableForDates(item.equipmentId, startDate, endDate, item.quantity);
  });
  const hasUnavailableSelected = unavailableSelectedEquipment.length > 0;

  const handleStudentSaveBooking = async (bookingId, updates, originalBooking) => {
    if (!originalBooking) {
      return { success: false, error: 'Booking not found' };
    }

    if (originalBooking.status === 'Returned' || originalBooking.status === 'Rejected') {
      return { success: false, error: 'This booking can no longer be edited.' };
    }

    const now = new Date();
    const bookingStart = parseDateOnly(originalBooking.startDate);
    const editCutoff = bookingStart ? new Date(bookingStart.getTime() - (STUDENT_EDIT_LOCK_HOURS * 60 * 60 * 1000)) : null;
    if (!bookingStart || !editCutoff || now >= editCutoff) {
      return { success: false, error: 'Bookings can only be edited up to 24 hours before the start date.' };
    }

    const updatedStartDate = updates.startDate || originalBooking.startDate;
    const updatedEndDate = updates.endDate || originalBooking.endDate;
    const updatedItems = Array.isArray(updates.items) ? updates.items : originalBooking.items;

    if (!Array.isArray(updatedItems) || updatedItems.length === 0) {
      return { success: false, error: 'At least one equipment item is required.' };
    }

    const parsedUpdatedStart = parseDateOnly(updatedStartDate);
    const parsedUpdatedEnd = parseDateOnly(updatedEndDate);
    if (!parsedUpdatedStart || !parsedUpdatedEnd || parsedUpdatedStart > parsedUpdatedEnd) {
      return { success: false, error: 'End date must be on or after the start date.' };
    }

    const availability = canFulfilBookingItems(updatedItems, updatedStartDate, updatedEndDate, bookingId);
    if (!availability.isAvailable) {
      return {
        success: false,
        error: `Updated booking conflicts with availability for: ${availability.conflicts.map((c) => c.equipmentName).join(', ')}`
      };
    }

    const changeLogEntries = [];
    const addChange = (field, oldValue, newValue) => {
      if (JSON.stringify(oldValue) === JSON.stringify(newValue)) return;
      changeLogEntries.push({
        timestamp: new Date().toISOString(),
        field,
        oldValue,
        newValue,
        studentName: originalBooking.studentName || 'Unknown Student'
      });
    };

    addChange('startDate', originalBooking.startDate, updatedStartDate);
    addChange('endDate', originalBooking.endDate, updatedEndDate);
    addChange('notes', originalBooking.notes || '', updates.notes || '');

    const originalItems = Array.isArray(originalBooking.items) ? originalBooking.items : [];
    const nextItems = Array.isArray(updatedItems) ? updatedItems : [];

    const toItemMap = (items) => {
      const map = new Map();
      items.forEach((item) => {
        if (!item?.equipmentId) return;
        map.set(item.equipmentId, {
          equipmentId: item.equipmentId,
          equipmentName: item.equipmentName || 'Unknown equipment',
          quantity: Number(item.quantity || 0)
        });
      });
      return map;
    };

    const originalMap = toItemMap(originalItems);
    const nextMap = toItemMap(nextItems);

    nextMap.forEach((nextItem, equipmentId) => {
      if (!originalMap.has(equipmentId)) {
        changeLogEntries.push({
          timestamp: new Date().toISOString(),
          field: 'equipmentAdded',
          oldValue: null,
          newValue: nextItem,
          studentName: originalBooking.studentName || 'Unknown Student'
        });
        return;
      }

      const originalItem = originalMap.get(equipmentId);
      if (Number(originalItem.quantity) !== Number(nextItem.quantity)) {
        changeLogEntries.push({
          timestamp: new Date().toISOString(),
          field: 'equipmentQtyChanged',
          oldValue: originalItem,
          newValue: nextItem,
          studentName: originalBooking.studentName || 'Unknown Student'
        });
      }
    });

    originalMap.forEach((originalItem, equipmentId) => {
      if (!nextMap.has(equipmentId)) {
        changeLogEntries.push({
          timestamp: new Date().toISOString(),
          field: 'equipmentRemoved',
          oldValue: originalItem,
          newValue: null,
          studentName: originalBooking.studentName || 'Unknown Student'
        });
      }
    });

    const nextChangeLog = [...(originalBooking.changeLog || []), ...changeLogEntries];

    const response = await updateBooking(bookingId, {
      startDate: updatedStartDate,
      endDate: updatedEndDate,
      notes: updates.notes ?? originalBooking.notes ?? '',
      items: updatedItems,
      changeLog: nextChangeLog,
      hasUnreviewedEdits: changeLogEntries.length > 0
    });

    if (!response.success) {
      return { success: false, error: response.error || 'Failed to save booking changes.' };
    }

    return { success: true };
  };

  const handleMarkBookingEditsReviewed = async (bookingId) => {
    try {
      await updateBooking(bookingId, {
        hasUnreviewedEdits: false,
        editsReviewedAt: new Date().toISOString()
      });
    } catch (error) {
      console.error('Failed to mark booking edits as reviewed:', error);
    }
  };

  // Ensure end date is never before start date
  useEffect(() => {
    if (bookingDetails.startDate && bookingDetails.endDate && bookingDetails.startDate > bookingDetails.endDate) {
      setBookingDetails(prev => ({
        ...prev,
        endDate: bookingDetails.startDate
      }));
    }
  }, [bookingDetails.startDate]);

  // Add these functions before the return statement
  const navigateMonth = (direction) => {
    const newDate = new Date(selectedDate);
    if (direction === 'prev') {
      newDate.setMonth(newDate.getMonth() - 1);
    } else {
      newDate.setMonth(newDate.getMonth() + 1);
    }
    setSelectedDate(newDate);
  };

  // Add this function near the top of the component, with other functions
  const scrollToBooking = (bookingId) => {
    const bookingElement = document.getElementById(`booking-${bookingId}`);
    if (bookingElement) {
      bookingElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      bookingElement.classList.add('highlight-booking');
      setTimeout(() => {
        bookingElement.classList.remove('highlight-booking');
      }, 2000);
    }
  };

  // Loading screen
  if (isLoading) {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <div className="logo-container">
              📷
            </div>
            <h2>Equipment Booking System</h2>
            <p>Loading application...</p>
          </div>
        </div>
      </div>
    );
  }
  
  // Login/Register screen
  if (!user) {
    return (
      <div className="login-container">
        {authView === 'login' ? (
          <Login 
            onLoginSuccess={handleLoginSuccess}
            switchToRegister={() => setAuthView('register')}
          />
        ) : (
          <Register 
            onRegistrationSuccess={handleRegistrationSuccess}
            switchToLogin={() => setAuthView('login')}
          />
        )}
      </div>
    );
  }
  
  console.log('Booking button rendered, disabled:', isBookingDisabled(), selectedEquipment.length === 0);
  
  return (
    <div className="app-container">
      <header className="app-header">
        <div className="logo">
          📷
          <h1>Camera Equipment Booking System</h1>
        </div>
        <div className="user-info">
          <div className="user">
            👤
            <span>{user.name} ({user.phoneNumber || user.staffId || 'Unknown ID'})</span>
          </div>
          <button className="logout-button animate-fade-in ripple-effect" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </header>
      
      <div className="tabs">
        <button 
          className={activeTab === 'student' ? 'tab active' : 'tab'}
          onClick={() => user.role !== 'staff' && setActiveTab('student')}
          disabled={user.role === 'staff'}
        >
          Student Portal
        </button>
        <button 
          className={activeTab === 'staff' ? 'tab active' : 'tab'}
          onClick={() => user.role === 'staff' && setActiveTab('staff')}
          disabled={user.role !== 'staff'}
        >
          Staff Portal
        </button>
      </div>
        
      {activeTab === 'student' && (
        <div className="portal-container student-portal">
          <div className="card equipment-list">
            <div className="card-header">
              <h3>
                📦
                Available Equipment
              </h3>
              <p>Browse and select equipment to book</p>
            </div>
            <div className="card-content">
              <div className="category-tabs">
                {sortedCategories.length > 0 ? (
                  sortedCategories.map(category => (
                    <button
                      key={category}
                      className={`category-tab ${activeCategory === category ? 'active' : ''}`}
                      onClick={() => setActiveCategory(category)}
                    >
                      {category}
                    </button>
                  ))
                ) : (
                  <div className="empty-message">No equipment categories available</div>
                )}
              </div>
              {activeCategory ? (
                <>
                  <div className="category-heading">
                    <h4>{activeCategory}</h4>
                  </div>
                  <table className="equipment-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Type</th>
                        <th>Available</th>
                        <th>Total</th>
                        <th>Status</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {categoryEquipment.map(item => {
                        const startDate = bookingDetails.startDate;
                        const endDate = bookingDetails.endDate || bookingDetails.startDate;
                        const availability = getEquipmentAvailabilityStatus(item, startDate, endDate);
                        const isSelected = selectedEquipment.some(eq => eq.equipmentId === item.id);
                        return (
                          <tr key={item.id}>
                            <td>
                              <div 
                                style={{ 
                                  position: 'relative',
                                  cursor: item.imageUrl ? 'pointer' : 'default',
                                  display: 'flex',
                                  alignItems: 'center',
                                  padding: '4px 8px',
                                  borderRadius: '4px',
                                  transition: 'all 0.2s ease',
                                  ...(item.imageUrl && {
                                    ':hover': {
                                      backgroundColor: 'rgba(79, 195, 247, 0.1)'
                                    }
                                  })
                                }}
                                onMouseEnter={() => setHoveredItem(item)}
                                onMouseLeave={() => setHoveredItem(null)}
                              >
                                <span style={{
                                  borderBottom: item.imageUrl ? '1px dashed #4fc3f7' : 'none',
                                  color: item.imageUrl ? '#4fc3f7' : 'inherit',
                                  transition: 'all 0.2s ease'
                                }}>
                                  {item.name}
                                </span>
                                {hoveredItem?.id === item.id && item.imageUrl && (
                                  <div style={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '100%',
                                    transform: 'translateY(-50%)',
                                    zIndex: 1000,
                                    backgroundColor: '#252525',
                                    padding: '8px',
                                    borderRadius: '4px',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                                    width: '200px',
                                    marginLeft: '12px'
                                  }}>
                                    <img 
                                      src={item.imageUrl} 
                                      alt={item.name}
                                      style={{
                                        width: '100%',
                                        height: 'auto',
                                        borderRadius: '4px'
                                      }}
                                    />
                                  </div>
                                )}
                              </div>
                            </td>
                            <td>{item.type}</td>
                            <td>{availability.availableQty}</td>
                            <td>{item.totalQty}</td>
                            <td>
                              <span className={`badge ${
                                availability.status.includes('Available from') ? 'warning' :
                                availability.status === 'Available' ? 'success' :
                                'error'
                              }`}>
                                {availability.status}
                              </span>
                              {availability.hasOverdueUnreturned && (
                                <div className="next-available">
                                  Held past return date: awaiting staff check-in
                                </div>
                              )}
                            </td>
                            <td>
                              <button
                                className={`button small ${isSelected ? 'secondary' : 'primary'}`}
                                onClick={() => handleSelectEquipment(item)}
                                disabled={availability.status === 'Out of Stock' && !availability.status.includes('Available from')}
                              >
                                {isSelected ? 'Remove' : 'Add'}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </>
              ) : (
                <div className="empty-message">Select a category to view equipment</div>
              )}
            </div>
          </div>
            
          <div className="card booking-form">
            <div className="card-header">
              <h3>
                📅
                Book Equipment
              </h3>
              <p>Enter project details and select dates</p>
            </div>
            <div className="card-content">
              <div className="form-group">
                <label>Production Name</label>
                <input 
                  type="text" 
                  name="productionName"
                  value={bookingDetails.productionName}
                  onChange={handleBookingDetailsChange}
                  placeholder="Enter production name"
                />
              </div>
              
              <div className="form-group">
                <label>Class</label>
                <select
                  name="className"
                  value={bookingDetails.className}
                  onChange={handleBookingDetailsChange}
                >
                  <option value="">Select class</option>
                  <option value="Cert 3 Lismore">Cert 3 Lismore</option>
                  <option value="Cert 3 Byron">Cert 3 Byron</option>
                  <option value="Cert 4">Cert 4</option>
                  <option value="Diploma">Diploma</option>
                </select>
              </div>
              
              <div className="form-group">
                <label>Teacher on Shoot (Optional)</label>
                <select
                  name="teacherOnShoot"
                  value={bookingDetails.teacherOnShoot}
                  onChange={handleBookingDetailsChange}
                >
                  <option value="">Select a teacher</option>
                  {teachers.map(teacher => (
                    <option key={teacher.id} value={teacher.id}>
                      {teacher.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Start Date</label>
                <input
                  type="date"
                  name="startDate"
                  value={bookingDetails.startDate}
                  onChange={handleBookingDetailsChange}
                  min={todayStr}
                />
              </div>

              <div className="form-group">
                <label>End Date</label>
                <input
                  type="date"
                  name="endDate"
                  value={bookingDetails.endDate}
                  onChange={handleBookingDetailsChange}
                  min={bookingDetails.startDate || todayStr}
                />
              </div>

              <div className="form-group">
                <label>Notes (Optional)</label>
                <textarea
                  name="notes"
                  value={bookingDetails.notes}
                  onChange={handleBookingDetailsChange}
                  placeholder="Add any additional notes or requirements"
                  className="notes-textarea"
                />
              </div>

              {/* Selected Equipment List */}
              {selectedEquipment.length > 0 && (
                <div className="selected-equipment-list">
                  <h4>Selected Equipment</h4>
                  {selectedEquipment.map(item => {
                    const requestedQty = Number(item.quantity || 1);
                    const dynamicAvailableQty = getAvailableQtyForDates(
                      item.equipmentId,
                      bookingDetails.startDate,
                      bookingDetails.endDate || bookingDetails.startDate
                    );
                    const isUnavailable = !isEquipmentAvailableForDates(
                      item.equipmentId,
                      bookingDetails.startDate,
                      bookingDetails.endDate || bookingDetails.startDate,
                      requestedQty
                    );
                    return (
                      <div key={item.equipmentId} className={`selected-equipment-item${isUnavailable ? ' unavailable' : ''}`} style={isUnavailable ? { border: '2px solid #ef4444', background: 'rgba(239,68,68,0.08)' } : {}}>
                        <span className="selected-equipment-name">{item.equipmentName}</span>
                        <div className="quantity-control">
                          <label>Qty:</label>
                          <input
                            type="number"
                            min={1}
                            max={Math.max(1, dynamicAvailableQty)}
                            value={Math.min(item.quantity, Math.max(1, dynamicAvailableQty))}
                            onChange={e => updateEquipmentQuantity(item.equipmentId, parseInt(e.target.value))}
                          />
                          <span className="max-qty">/ {dynamicAvailableQty} available</span>
                        </div>
                        <button
                          className="remove-item"
                          onClick={() => handleSelectEquipment({ id: item.equipmentId })}
                        >
                          Remove
                        </button>
                        {isUnavailable && null}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Warning message above the booking button */}
              {hasUnavailableSelected && (
                <div className="unavailable-selected-warning" style={{ color: '#ef4444', background: 'rgba(239,68,68,0.08)', padding: '8px', borderRadius: '4px', marginBottom: '1rem', textAlign: 'center', fontWeight: 500 }}>
                  Some selected equipment is not available for the chosen dates. Please remove the affected items or change your booking dates.
                </div>
              )}

              <button 
                className="button primary book-button"
                onClick={bookEquipment}
                disabled={isBookingDisabled() || selectedEquipment.length === 0}
              >
                {isBookingLoading ? 'Submitting...' : 'Book Selected Equipment'}
              </button>
              {bookingSuccess && (
                (() => { console.log('Booking success message shown'); return null; })(),
                <div className="success-message animate-fade-in" style={{ marginTop: '1rem', padding: '1rem', backgroundColor: 'var(--success-bg)', color: 'var(--success-color)', borderRadius: '4px', textAlign: 'center' }}>
                  ✓ Booking submitted successfully! You can view your booking in the "Current Bookings" tab once approved by staff.
                </div>
              )}
            </div>
          </div>
            
          {/* Student's bookings view selector */}
          <div className="card bookings">
            <div className="card-header">
              <h3>
                📅
                Your Bookings
              </h3>
              <p>View your current and past bookings</p>
              <div className="view-mode-tabs">
                <button
                  className={`view-mode-tab ${studentBookingView === 'current' ? 'active animate-fade-in' : 'animate-fade-in'}`}
                  onClick={() => setStudentBookingView('current')}
                >
                  Current Bookings
                </button>
                <button
                  className={`view-mode-tab ${studentBookingView === 'previous' ? 'active animate-fade-in' : 'animate-fade-in'}`}
                  onClick={() => setStudentBookingView('previous')}
                >
                  Previous Bookings
                </button>
              </div>
            </div>
            <div className="card-content">
              {studentBookingView === 'current' ? (
                <EnhancedBookingsTable 
                  bookings={currentBookings}
                  studentId={user?.phoneNumber || user?.staffId}
                  isStaff={false}
                  equipmentData={equipment}
                  onBookingUpdated={handleBookingUpdated}
                  onStudentSaveBooking={handleStudentSaveBooking}
                  displayMode="current"
                  approveBooking={() => {}}
                  returnEquipment={() => {}}
                  rejectBooking={() => {}}
                  tableId="current-bookings"
                />
              ) : (
                <EnhancedBookingsTable 
                  bookings={previousBookings}
                  studentId={user?.phoneNumber || user?.staffId}
                  isStaff={false}
                  equipmentData={equipment}
                  onBookingUpdated={handleBookingUpdated}
                  onStudentSaveBooking={handleStudentSaveBooking}
                  displayMode="previous"
                  approveBooking={() => {}}
                  returnEquipment={() => {}}
                  rejectBooking={() => {}}
                  tableId="previous-bookings"
                />
              )}
            </div>
          </div>
        </div>
      )}
        
      {activeTab === 'staff' && (
        <div className="portal-container staff-portal">
          <div className="view-mode-tabs" style={{ marginBottom: '20px' }}>
            <button 
              className={`view-mode-tab ${staffView === 'bookings' ? 'active animate-fade-in' : 'animate-fade-in'}`}
              onClick={() => setStaffView('bookings')}
            >
              Bookings
            </button>
            <button 
              className={`view-mode-tab ${staffView === 'equipment' ? 'active animate-fade-in' : 'animate-fade-in'}`}
              onClick={() => setStaffView('equipment')}
            >
              Equipment
            </button>
          </div>
          {staffView === 'bookings' && (
            <>
              {/* Calendar View */}
              <div className="card calendar-view">
                <div className="card-header">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                    <div>
                      <h3>
                        📅
                        Booking Calendar
                      </h3>
                      <p>View all active bookings in a calendar format</p>
                    </div>
                    <button 
                      className="collapse-button"
                      onClick={() => setIsCalendarCollapsed(!isCalendarCollapsed)}
                    >
                      <svg viewBox="0 0 24 24">
                        <polyline points={isCalendarCollapsed ? "6 9 12 15 18 9" : "18 15 12 9 6 15"}></polyline>
                      </svg>
                    </button>
                  </div>
                </div>
                {!isCalendarCollapsed && (
                  <div className="card-content">
                    <div className="calendar-controls">
                      <button className="calendar-nav-button" onClick={() => navigateMonth('prev')}>
                        <svg viewBox="0 0 24 24">
                          <polyline points="15 6 9 12 15 18"></polyline>
                        </svg>
                      </button>
                      <h4>{selectedDate.toLocaleString('default', { month: 'long', year: 'numeric' })}</h4>
                      <button className="calendar-nav-button" onClick={() => navigateMonth('next')}>
                        <svg viewBox="0 0 24 24">
                          <polyline points="9 6 15 12 9 18"></polyline>
                        </svg>
                      </button>
                    </div>
                    <div className="calendar-grid">
                      <div className="calendar-day-names">
                        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                          <div key={day} className="calendar-day-name">{day}</div>
                        ))}
                      </div>
                      <div className="calendar-days">
                        {(() => {
                          const today = new Date();
                          const firstDay = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
                          const lastDay = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0);
                          const days = [];
                          
                          // Add empty cells for days before the first day of the month
                          for (let i = 0; i < firstDay.getDay(); i++) {
                            days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
                          }
                          
                          // Add cells for each day of the month
                          for (let day = 1; day <= lastDay.getDate(); day++) {
                            const currentDate = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), day);
                            const dateStr = currentDate.toISOString().split('T')[0];
                            
                            // Find bookings for this day
                            const dayBookings = bookings.filter(booking => {
                              // Convert dates to start of day for accurate comparison
                              const bookingStart = new Date(booking.startDate);
                              bookingStart.setHours(0, 0, 0, 0);
                              const bookingEnd = new Date(booking.endDate);
                              bookingEnd.setHours(0, 0, 0, 0);
                              const currentDateStart = new Date(currentDate);
                              currentDateStart.setHours(0, 0, 0, 0);
                              
                              // Check if current date falls within booking period
                              const isWithinBookingPeriod = currentDateStart >= bookingStart && 
                                                          currentDateStart <= bookingEnd;
                              
                              // Only include active bookings
                              return isWithinBookingPeriod && 
                                     (booking.status === 'Approved' || booking.status === 'Pending');
                            });
                            
                            const isToday = currentDate.toDateString() === today.toDateString();
                            
                            days.push(
                              <div 
                                key={day} 
                                className={`calendar-day ${isToday ? 'today' : ''} ${dayBookings.length > 0 ? 'has-bookings' : ''}`}
                              >
                                <div className="calendar-date">{day}</div>
                                {dayBookings.length > 0 && (
                                  <div className="calendar-bookings">
                                    {dayBookings.map(booking => {
                                      // Determine class type for color coding
                                      let classType = '';
                                      if (booking.className?.toLowerCase().includes('cert 3')) {
                                        classType = 'cert3';
                                      } else if (booking.className?.toLowerCase().includes('cert 4')) {
                                        classType = 'cert4';
                                      } else if (booking.className?.toLowerCase().includes('diploma')) {
                                        classType = 'diploma';
                                      }
                                      
                                      return (
                                        <div
                                          key={booking.id}
                                          className={`booking-item ${classType}`}
                                          onClick={() => scrollToBooking(booking.id)}
                                          style={{ cursor: 'pointer' }}
                                        >
                                          {booking.productionName}
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          }
                          
                          return days;
                        })()}
                      </div>
                    </div>
                    <div className="calendar-legend">
                      <div className="legend-item">
                        <div className="legend-color today"></div>
                        <span>Today</span>
                      </div>
                      <div className="legend-item">
                        <div className="legend-color has-bookings"></div>
                        <span>Has Bookings</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Booking Requests Table (Pending only) */}
              <EnhancedBookingsTable 
                bookings={Array.isArray(bookings) ? bookings.filter(b => b && b.status === 'Pending') : []}
                studentId={user?.phoneNumber || user?.staffId}
                isStaff={true}
                equipmentData={equipment}
                approveBooking={handleApproveBooking}
                returnEquipment={returnEquipment}
                onBookingUpdated={handleBookingUpdated}
                rejectBooking={handleRejectBooking}
                onMarkBookingEditsReviewed={handleMarkBookingEditsReviewed}
                tableId="pending-bookings"
              />
              {/* Active Bookings Table (Approved, not returned) */}
              <EnhancedBookingsTable 
                bookings={Array.isArray(bookings) ? bookings.filter(b => b && b.status === 'Approved' && b.status !== 'Returned') : []}
                studentId={user?.phoneNumber || user?.staffId}
                isStaff={true}
                equipmentData={equipment}
                approveBooking={handleApproveBooking}
                returnEquipment={returnEquipment}
                onBookingUpdated={handleBookingUpdated}
                rejectBooking={handleRejectBooking}
                onMarkBookingEditsReviewed={handleMarkBookingEditsReviewed}
                customHeader="Active Bookings"
                tableId="active-bookings"
              />
              {/* Booking History Table */}
              <BookingHistory equipmentData={equipment} isStaff={user.role === 'staff'} />
            </>
          )}
          {staffView === 'equipment' && (
            <>
              {/* Equipment Inventory */}
              <div className="card inventory">
                <div className="card-header">
                  <h3>
                    📦
                    Equipment Inventory
                  </h3>
                  <p>Manage your equipment inventory</p>
                </div>
                <div className="card-content">
                  <div className="category-tabs">
                    {sortedCategories.length > 0 ? (
                      sortedCategories.map(category => (
                        <button
                          key={category}
                          className={`category-tab ${activeCategory === category ? 'active' : ''}`}
                          onClick={() => setActiveCategory(category)}
                        >
                          {category}
                        </button>
                      ))
                    ) : (
                      <div className="empty-message">No equipment categories available</div>
                    )}
                  </div>
                  {activeCategory ? (
                    <>
                      <div className="category-heading">
                        <h4>{activeCategory}</h4>
                      </div>
                      <div className="equipment-table-wrapper">
                        <table className="equipment-table">
                          <thead>
                            <tr>
                              <th>Name</th>
                              <th>Type</th>
                              <th>Available</th>
                              <th>Total</th>
                              <th>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {equipment
                              .filter(item => item.category === activeCategory)
                              .map(item => (
                                <tr key={item.id}>
                                  <td data-label="Name">{item.name}</td>
                                  <td data-label="Type">{item.type}</td>
                                  <td data-label="Available">{item.availableQty}</td>
                                  <td data-label="Total">{item.totalQty}</td>
                                  <td data-label="Status">
                                    <span className={`status-badge ${item.availableQty > 0 ? 'available' : 'out-of-stock'}`}>
                                      {item.availableQty > 0 ? 'Available' : 'Out of Stock'}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  ) : (
                    <div className="empty-message">
                      {sortedCategories.length > 0 
                        ? "No category selected. Please select a category from above."
                        : "No equipment categories available. Please add some equipment first."}
                    </div>
                  )}
                </div>
              </div>
              {/* Equipment Management */}
              <div className="card">
                <div className="card-header">
                  <h3>
                    🛠️
                    Equipment Management
                  </h3>
                  <p>Add, edit, or remove equipment from the inventory</p>
                </div>
                <div className="card-content">
                  <EquipmentManager />
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default App;